import { track } from "jiffy-analytics";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

import { useAiDesign } from "@design-lab/shared/lib/config";
import { activeMenuAtom, MENU_IDS, type MenuId } from "./state";
import { useTokenManagement } from "./tokenManagement";
import { useArtworkDetection } from "./useArtworkDetection";
import { useArtworkSelection } from "./useArtworkSelection";

type UseMenuStateOptions = {
  enableInitialization?: boolean;
};

export function useMenuState(_initialMenu?: MenuId, options: UseMenuStateOptions = {}) {
  const { enableInitialization = false } = options;
  const [activeMenu, setActiveMenu] = useAtom(activeMenuAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const { aiCreditsBalance } = useTokenManagement();
  const aiDesign = useAiDesign();
  const { restorableSides } = useArtworkDetection();
  const { setSelection } = useArtworkSelection();
  const hasHandledInitializationRef = useRef(false);

  const designMenuParam = searchParams.get("design-menu") as MenuId | null;
  const isValidDesignMenu = designMenuParam && Object.values(MENU_IDS).includes(designMenuParam);

  useEffect(() => {
    // Skip if initialization has already been handled
    if (enableInitialization && hasHandledInitializationRef.current) return;

    if (!enableInitialization) return;

    // Check for AI design config
    if (aiDesign) {
      setActiveMenu(MENU_IDS.AI_DESIGN);
      hasHandledInitializationRef.current = true;
      return;
    }

    // Handle artwork restoration (form state after reload)
    if (restorableSides.length > 0) {
      setActiveMenu(MENU_IDS.UPLOAD_IMPORT);
      if (enableInitialization) {
        restorableSides.forEach(side => {
          if (side.artworkId) setSelection(side.id, MENU_IDS.UPLOAD_IMPORT, side.artworkId);
        });
      }
      hasHandledInitializationRef.current = true;
      return;
    }

    // Check if there's a persisted menu in URL (on page refresh)
    if (isValidDesignMenu && designMenuParam) {
      setActiveMenu(designMenuParam);
      hasHandledInitializationRef.current = true;
      return;
    }

    setActiveMenu(prev => {
      const defaultMenu = MENU_IDS.UPLOAD_IMPORT;
      return prev === undefined ? defaultMenu : prev;
    });
  }, [
    enableInitialization,
    aiCreditsBalance,
    aiDesign,
    restorableSides,
    setActiveMenu,
    setSelection,
    isValidDesignMenu,
    designMenuParam
  ]);

  const currentActiveMenu = activeMenu ?? MENU_IDS.UPLOAD_IMPORT;

  // Update URL when menu changes
  useEffect(() => {
    if (!activeMenu) return;

    setSearchParams(
      prevParams => {
        const newParams = new URLSearchParams(prevParams);

        // Set design-menu parameter for persistence (skip for default upload-import)
        if (activeMenu === MENU_IDS.UPLOAD_IMPORT) {
          newParams.delete("design-menu");
        } else {
          newParams.set("design-menu", activeMenu);
        }

        return newParams;
      },
      { replace: true }
    );
  }, [activeMenu, setSearchParams]);

  const handleMenuChange = useCallback(
    (menuId: MenuId) => {
      if (menuId === MENU_IDS.COMMUNITY_ART) {
        track("community_art_tab_opened", {
          page: "design_lab"
        });
      }
      setActiveMenu(menuId);
    },
    [setActiveMenu]
  );

  // TODO rethink this
  return {
    activeMenu: currentActiveMenu,
    handleMenuChange,
    isRedirect: false,
    showLoader: false
  };
}
